import { Box, ButtonBase, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

/** How long a picked row's border takes to change colour. */
const CHANGE_MS = 240;

/**
 * One answer attached under a message from Keni, full width with its label
 * centred: the buttons ChatMenuMessage stacks under its bubble.
 *
 * Three looks. An answer, in Marine Green. A quieter one, in grey, for
 * something to do instead of answering (How we use AI) or the answer we would
 * rather they did not pick (I'd rather not). And picked, with a Marine Green
 * border and a tick.
 */
const ChatOption = ({
  label,
  quiet = false,
  picked = false,
  icon,
  disabled = false,
  onClick,
}: {
  label: string;
  /** Grey rather than green: a quieter row. */
  quiet?: boolean;
  /** The one they picked: a green border and a tick. */
  picked?: boolean;
  /** An icon before the label, like See options' list. A tick replaces it once picked. */
  icon?: string;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <ButtonBase
    disabled={disabled}
    onClick={onClick}
    sx={{
      width: '100%',
      px: 2,
      py: 1.25,
      gap: 1,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: picked ? 'primary.main' : 'custom.neutral.200',
      borderRadius: `${radius.md}px`,
      transition: `border-color ${CHANGE_MS}ms`,
      '&:hover': { borderColor: 'custom.marineGreen.300' },
      // ButtonBase marks keyboard focus but draws nothing for it. The same
      // inset ring as a tappable card's (theme.tsx), so they can see where
      // they are.
      '&.Mui-focusVisible': {
        outline: (theme) => `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '-2px',
      },
    }}
  >
    {(picked || icon) && (
      <Box sx={{ display: 'flex', color: 'primary.main' }}>
        <Icon icon={(picked ? 'check' : icon) as never} fontSize={14} />
      </Box>
    )}
    {quiet ? (
      <Typography sx={{ color: 'text.secondary' }} variant='body1'>
        {label}
      </Typography>
    ) : (
      <Typography sx={{ color: 'primary.main' }} variant='subtitle1'>
        {label}
      </Typography>
    )}
  </ButtonBase>
);

ChatOption.displayName = 'ChatOption';
export default ChatOption;
